import React, { useEffect, useState } from 'react';
import { App, Button, Col, Form, Input, InputNumber, Modal, Row, Select, Switch } from 'antd';
import { DeleteOutlined, PlusOutlined } from '@ant-design/icons';
import { useQueryClient } from '@tanstack/react-query';
import hrmsService, { hrmsKeys } from '@/services/hrms.service';
import { parseJsonArray, recruitErrorMessage } from './shared/recruitUi';

const TYPES = ['Competency', 'Technical', 'Behavioral', 'Language', 'Psychometric'];

interface Props {
  open: boolean;
  template?: any | null;
  onClose: () => void;
}

/** Scorecard template master (popup allowed for masters). Criteria drive both assessments and interview feedback. */
const AssessmentTemplateModal: React.FC<Props> = ({ open, template, onClose }) => {
  const { message } = App.useApp();
  const queryClient = useQueryClient();
  const [form] = Form.useForm();
  const [saving, setSaving] = useState(false);
  const isDefault = template?.template_code === 'COMP_DEFAULT';

  useEffect(() => {
    if (!open) return;
    if (template) {
      form.setFieldsValue({
        ...template,
        passing_score: Number(template.passing_score ?? 70),
        questions_json: parseJsonArray(template.questions_json).map((q: any) => ({
          ...q,
          weight: Number(q.weight ?? 1),
          max_rating: Number(q.max_rating ?? 5),
        })),
      });
    } else {
      form.resetFields();
      form.setFieldsValue({
        assessment_type: 'Competency',
        passing_score: 70,
        is_active: true,
        questions_json: [{ section: 'General', text: '', weight: 1, max_rating: 5 }],
      });
    }
  }, [open, template, form]);

  const save = async () => {
    const v = await form.validateFields();
    setSaving(true);
    try {
      if (template) await hrmsService.updateAssessmentTemplate(template.id, v);
      else await hrmsService.createAssessmentTemplate(v);
      message.success(template ? 'Template updated' : 'Template created');
      await queryClient.invalidateQueries({ queryKey: hrmsKeys.assessmentTemplates() });
      onClose();
    } catch (e: any) {
      message.error(recruitErrorMessage(e));
    } finally {
      setSaving(false);
    }
  };

  return (
    <Modal
      open={open}
      title={template ? `Edit ${template.template_name}` : 'New scorecard template'}
      okText="Save"
      onOk={save}
      confirmLoading={saving}
      onCancel={onClose}
      width={820}
      destroyOnClose
    >
      <Form form={form} layout="vertical">
        <Row gutter={12}>
          <Col span={12}>
            <Form.Item name="template_name" label="Name" rules={[{ required: true, whitespace: true }]}>
              <Input placeholder="e.g. Site engineer technical interview" />
            </Form.Item>
          </Col>
          <Col span={6}>
            <Form.Item name="assessment_type" label="Type">
              <Select options={TYPES.map((t) => ({ value: t, label: t }))} />
            </Form.Item>
          </Col>
          <Col span={4}>
            <Form.Item name="passing_score" label="Pass mark %" rules={[{ required: true }]}>
              <InputNumber min={0} max={100} style={{ width: '100%' }} />
            </Form.Item>
          </Col>
          <Col span={2}>
            <Form.Item name="is_active" label="Active" valuePropName="checked">
              <Switch disabled={isDefault} />
            </Form.Item>
          </Col>
          <Col span={24}>
            <Form.Item name="description" label="Description">
              <Input.TextArea rows={2} />
            </Form.Item>
          </Col>
        </Row>
        <Form.List name="questions_json" rules={[{ validator: async (_, v) => (v?.length ? undefined : Promise.reject(new Error('Add at least one criterion'))) }]}>
          {(fields, { add, remove }, { errors }) => (
            <>
              <Row gutter={8} style={{ fontWeight: 500, marginBottom: 4 }}>
                <Col span={5}>Section</Col>
                <Col span={11}>Criterion</Col>
                <Col span={3}>Weight</Col>
                <Col span={3}>Scale (max)</Col>
              </Row>
              {fields.map((f) => (
                <Row gutter={8} key={f.key}>
                  <Form.Item name={[f.name, 'code']} hidden>
                    <Input />
                  </Form.Item>
                  <Col span={5}>
                    <Form.Item name={[f.name, 'section']}>
                      <Input placeholder="General" />
                    </Form.Item>
                  </Col>
                  <Col span={11}>
                    <Form.Item name={[f.name, 'text']} rules={[{ required: true, whitespace: true, message: 'Required' }]}>
                      <Input placeholder="What is being rated" />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[f.name, 'weight']}>
                      <InputNumber min={0.1} max={100} step={0.5} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={3}>
                    <Form.Item name={[f.name, 'max_rating']}>
                      <InputNumber min={2} max={10} style={{ width: '100%' }} />
                    </Form.Item>
                  </Col>
                  <Col span={2}>
                    <Button type="text" danger icon={<DeleteOutlined />} onClick={() => remove(f.name)} />
                  </Col>
                </Row>
              ))}
              <Form.ErrorList errors={errors} />
              <Button type="dashed" icon={<PlusOutlined />} onClick={() => add({ section: 'General', weight: 1, max_rating: 5 })}>
                Add criterion
              </Button>
            </>
          )}
        </Form.List>
      </Form>
    </Modal>
  );
};

export default AssessmentTemplateModal;
